// src/finance/FinanceView.tsx
//
// BekalNanti — Milestone P3.6: Finance / Keuangan View
// Shared Household Financial Reality workspace providing transparent cashflow capacity.

import React from 'react'
import { FinancialRealityData } from '../journey/types'
import { formatIDR, formatCompactIDR } from '../lib/financialCalculation'
import {
  DollarSign,
  TrendingUp,
  Receipt,
  CreditCard,
  PiggyBank,
  Edit2,
  Plus,
  Scale,
} from 'lucide-react'

interface FinanceViewProps {
  financialReality?: FinancialRealityData
  onOpenFinancialModal: () => void
}

export const FinanceView: React.FC<FinanceViewProps> = ({
  financialReality,
  onOpenFinancialModal,
}) => {
  const summary = financialReality?.summary
  const incomes = financialReality?.incomes || []
  const expenses = financialReality?.expenses || []
  const debts = financialReality?.debts || []
  const savings = financialReality?.savings || []
  const obligations = financialReality?.obligations || []

  const hasData =
    incomes.length > 0 ||
    expenses.length > 0 ||
    debts.length > 0 ||
    savings.length > 0 ||
    obligations.length > 0

  const netCapacity = summary?.netMonthlyCapacity ?? 0
  const isSurplus = netCapacity >= 0

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Top Banner / Heading */}
      <div className="rounded-3xl bg-gradient-to-br from-teal-50/80 via-white to-emerald-50/40 border border-teal-100/80 p-5 shadow-2xs space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-teal-200 text-[#0F766E] text-[11px] font-bold shadow-2xs">
            <DollarSign size={14} className="text-[#0EA5A4]" />
            <span>Kondisi Keuangan Keluarga</span>
          </span>

          <button
            type="button"
            onClick={onOpenFinancialModal}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-all cursor-pointer shadow-2xs active:scale-95"
          >
            <Edit2 size={13} />
            <span>Atur Realita Finansial</span>
          </button>
        </div>

        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            Kapasitas Keuangan Bersama
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Realita keuangan dikelola bersama di tingkat rumah tangga untuk menopang seluruh rencana keluarga secara objektif.
          </p>
        </div>
      </div>

      {/* Net Monthly Capacity Highlight Card */}
      <div className="rounded-3xl border border-teal-200/80 bg-gradient-to-br from-white via-white to-teal-50/30 p-5 shadow-sm space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Kapasitas Arus Kas Bulanan
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <h2
                className={`text-2xl sm:text-3xl font-black ${
                  isSurplus ? 'text-[#0F766E]' : 'text-rose-600'
                }`}
              >
                {formatIDR(netCapacity)}
              </h2>
              <span className="text-xs text-slate-400 font-semibold">/ bulan</span>
            </div>
          </div>

          <span
            className={`px-3 py-1 rounded-full text-xs font-bold border ${
              isSurplus
                ? 'bg-teal-50 text-[#0F766E] border-teal-200'
                : 'bg-rose-50 text-rose-700 border-rose-200'
            }`}
          >
            {isSurplus ? 'Surplus Bulanan' : 'Defisit Arus Kas'}
          </span>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed">
          {isSurplus
            ? 'Jumlah ini adalah sisa kapasitas bersih bulanan keluarga setelah seluruh biaya hidup, cicilan, dan komitmen rutin terpenuhi.'
            : 'Pengeluaran saat ini melampaui pemasukan bulanan. Periksa pengeluaran diskresioner atau cicilan aktif.'}
        </p>

        {/* 4 Key Numerical Blocks */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2 border-t border-slate-100">
          <div className="p-3 rounded-2xl bg-slate-50/70 border border-slate-200/60">
            <span className="text-[10px] font-bold text-slate-400 uppercase block">Pemasukan</span>
            <span className="text-sm font-bold text-slate-800 mt-0.5 block truncate">
              {formatCompactIDR(summary?.totalMonthlyIncome || 0)}
            </span>
            <span className="text-[10px] text-slate-400">{incomes.length} pos</span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-50/70 border border-slate-200/60">
            <span className="text-[10px] font-bold text-slate-400 uppercase block">Pengeluaran</span>
            <span className="text-sm font-bold text-slate-800 mt-0.5 block truncate">
              {formatCompactIDR(summary?.totalMonthlyExpenses || 0)}
            </span>
            <span className="text-[10px] text-slate-400">{expenses.length} pos</span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-50/70 border border-slate-200/60">
            <span className="text-[10px] font-bold text-slate-400 uppercase block">Cicilan &amp; Beban</span>
            <span className="text-sm font-bold text-slate-800 mt-0.5 block truncate">
              {formatCompactIDR(
                (summary?.totalMonthlyDebtPayments || 0) + (summary?.totalMonthlyObligations || 0)
              )}
            </span>
            <span className="text-[10px] text-slate-400">
              {debts.length + obligations.length} komitmen
            </span>
          </div>

          <div className="p-3 rounded-2xl bg-slate-50/70 border border-slate-200/60">
            <span className="text-[10px] font-bold text-slate-400 uppercase block">Tabungan Awal</span>
            <span className="text-sm font-bold text-teal-800 mt-0.5 block truncate">
              {formatCompactIDR(summary?.availableSavings || 0)}
            </span>
            <span className="text-[10px] text-slate-400">{savings.length} simpanan</span>
          </div>
        </div>
      </div>

      {/* Breakdown Lists */}
      {hasData ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Rincian Pos Keuangan</h2>
            <button
              type="button"
              onClick={onOpenFinancialModal}
              className="text-[#0EA5A4] hover:text-[#0F766E] text-xs font-semibold cursor-pointer"
            >
              Ubah Data di Ruang Kerja
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Incomes Card */}
            <div className="rounded-3xl border border-slate-200/80 bg-white p-4 space-y-2.5 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <TrendingUp size={14} className="text-emerald-500" />
                  <span>Pemasukan ({incomes.length})</span>
                </span>
                <span className="text-xs font-bold text-emerald-600">
                  {formatCompactIDR(summary?.totalMonthlyIncome || 0)}/bln
                </span>
              </div>
              <div className="space-y-1">
                {incomes.map((i) => (
                  <div
                    key={i.id}
                    className="flex items-center justify-between text-xs py-1 px-2 rounded-xl bg-slate-50"
                  >
                    <span className="text-slate-700 truncate">{i.name}</span>
                    <span className="font-semibold text-slate-900">{formatIDR(i.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expenses Card */}
            <div className="rounded-3xl border border-slate-200/80 bg-white p-4 space-y-2.5 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <Receipt size={14} className="text-amber-500" />
                  <span>Pengeluaran ({expenses.length})</span>
                </span>
                <span className="text-xs font-bold text-amber-700">
                  {formatCompactIDR(summary?.totalMonthlyExpenses || 0)}/bln
                </span>
              </div>
              <div className="space-y-1">
                {expenses.map((e) => (
                  <div
                    key={e.id}
                    className="flex items-center justify-between text-xs py-1 px-2 rounded-xl bg-slate-50"
                  >
                    <span className="text-slate-700 truncate">{e.name}</span>
                    <span className="font-semibold text-slate-900">{formatIDR(e.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Debts & Obligations Card */}
            <div className="rounded-3xl border border-slate-200/80 bg-white p-4 space-y-2.5 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <CreditCard size={14} className="text-rose-500" />
                  <span>Cicilan &amp; Kewajiban ({debts.length + obligations.length})</span>
                </span>
                <span className="text-xs font-bold text-rose-600">
                  {formatCompactIDR(
                    (summary?.totalMonthlyDebtPayments || 0) + (summary?.totalMonthlyObligations || 0)
                  )}/bln
                </span>
              </div>
              <div className="space-y-1">
                {[...debts, ...obligations].map((item: any) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between text-xs py-1 px-2 rounded-xl bg-slate-50"
                  >
                    <span className="text-slate-700 truncate">{item.name}</span>
                    <span className="font-semibold text-slate-900">
                      {formatIDR(item.monthlyPayment || item.amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Savings Card */}
            <div className="rounded-3xl border border-slate-200/80 bg-white p-4 space-y-2.5 shadow-2xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <PiggyBank size={14} className="text-teal-600" />
                  <span>Tabungan &amp; Simpanan ({savings.length})</span>
                </span>
                <span className="text-xs font-bold text-[#0F766E]">
                  {formatCompactIDR(summary?.availableSavings || 0)}
                </span>
              </div>
              <div className="space-y-1">
                {savings.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between text-xs py-1 px-2 rounded-xl bg-slate-50"
                  >
                    <span className="text-slate-700 truncate">{s.name}</span>
                    <span className="font-semibold text-slate-900">{formatIDR(s.balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-12 px-6 rounded-3xl border border-dashed border-slate-200 bg-white text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center mx-auto">
            <Scale size={24} />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-slate-900">
              Kondisi Keuangan Belum Dicatat
            </h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
              Catat pemasukan dan pengeluaran keluarga untuk mengetahui sisa kapasitas tabungan yang dapat dialokasikan.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenFinancialModal}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
          >
            <Plus size={14} />
            <span>Petakan Keuangan Keluarga</span>
          </button>
        </div>
      )}
    </div>
  )
}
